import {
  Box,
  Typography,
  useForm,
  Spinner,
  Modal,
  ModalTrigger,
  ModalBody,
  ModalContent,
  Button,
  ModalClose,
} from "@orion/ui";
import { useLoginForm } from "../hooks";
import { useTranslation } from "react-i18next";
import { useErrorContext } from "../../_shared/context/ErrorContext";
import { LoginFormType, loginSchema } from "../schemas/login";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import LoginFormComponent from "../components/LoginFormComponent";
import { ErrorType } from "@delorean/_shared/domain/errorType";
import { Phone, Supporto } from "@orion/icons";
import { getEnvVar } from "@delorean/utils/env-utils";
const LoginForm = () => {
  const { t } = useTranslation();
  const { submit, loading, goToResetPassword, error: loginError } = useLoginForm();
  const { showError, hideError, error } = useErrorContext();

  const [openModal, setOpenModal] = useState(false);
  const isSupportModalEnabled = getEnvVar("VITE_CR_LOGIN_SUPPORT_NOV26", "false") === "true";
  const schema = loginSchema({ t, withCaptcha: false });
  const form = useForm<LoginFormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      contractCode: "",
      userCode: "",
      password: "",
    },
  });

  const onSubmitForm = async (values: LoginFormType) => {
    hideError();
    try {
      await submit(values);
    } catch (e) {
      console.error("Login submit error:", e);
      showError(t("errors.messageTwo"));
    }
  };

  useEffect(() => {
    /* credenziali errate: svuota tutti i campi del form */
    if (loginError === ErrorType.sessionExpired || loginError === ErrorType.softLocked || loginError === ErrorType.userLocked || loginError === ErrorType.wrongParams) {
      form.reset({ contractCode: "", userCode: "", password: "" });
    }
  }, [loginError]);

  useEffect(() => {
    /* focus on first input error if form is on error */
    if (Object.keys(form.formState.errors).length > 0) {
      showError(t("login.formValidation.requiredField"));
      const firstInvalidInput = document.querySelector(
        "form *[aria-invalid='true']"
      ) as HTMLInputElement;
      if (firstInvalidInput) {
        firstInvalidInput.focus();
      }
    }
  }, [Object.keys(form.formState.errors).length]);

  useEffect(() => {
    /* focus on error toast message if sersice is on error */
    if (error.visible && !Object.keys(form.formState.errors).length) {
      const errorMessageToast = document.getElementById("errorMessageToast");
      if (errorMessageToast) {
        errorMessageToast.focus();
      }
    }
  }, [error.visible]);

  return (
    <Box
      flex
      column
      my9
      data-landmark="1"
      role="form"
      style={{
        width: "576px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "40px",
        position: "relative",
        zIndex: 1
      }}
    >
      {loading ? (
        <Spinner size={"16"} />
      ) : (
        <>
          <Box
            flex
            gap3
            column
            itemsStart
            style={{
              width: "576px",
              height: "60px",
            }}
            aria-label={t("login.title") + " " + t("login.subtitle")}
          >
            {isSupportModalEnabled && (
            <Modal open={openModal} onOpenChange={(open) => setOpenModal(open)}>
              <ModalTrigger asChild><Box flex justifyEnd wFull gap5 pb10 cursor="pointer"><Supporto width="10" height="10" textSuccess100 /><Typography fontBold >{t("login.supportModal.requestSupport")}</Typography></Box></ModalTrigger>
              <ModalContent>
                <ModalBody>

                  <Box flex column gap10 wFull>
                    <Typography text2xl fontBold>
                      {t("login.supportModal.title")}
                    </Typography>
                    <Box flex column gap10 p10 wFull bgNeutral10 border1 borderSolid borderColor="neutral20" roundedLg>
                        <Box flex row itemsCenter gap5>
                          <Phone width="10" height="10" />
                          <Typography textLg fontBold>{t("login.supportModal.phoneCall")}</Typography>
                        </Box>
                      <Box flex row justifyBetween gap10 >
                        <Box flex column gap5 flexGrow="1">
                          <Box flex row itemsCenter gap5>
                            <Typography>{t("login.supportModal.fromItalyAndAbroad")}</Typography>
                            <Typography textLg fontBold>{t("login.supportModal.phoneNumber")}</Typography>
                          </Box>
                          <Typography textSm>
                            {t("login.supportModal.automaticAnswering")}
                          </Typography>

                          <Typography textSm textNeutral80 >
                            {t("login.supportModal.italianTimeZone")}
                          </Typography>

                        </Box>
                        <Box flex column gap5 flexShrink="0">
                          <Box flex column>
                            <Typography textSm>{t("login.supportModal.weekdays")}</Typography>
                            <Typography textSm fontBold textNeutral80>{t("login.supportModal.weekdaysHours")}</Typography>
                          </Box>
                          <Box flex column>
                            <Typography textSm>{t("login.supportModal.saturday")}</Typography>
                            <Typography textSm fontBold textNeutral80>{t("login.supportModal.saturdayHours")}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                  </Box>
                </ModalBody>
              </ModalContent>
            </Modal>
            )}
            <Typography text2xl fontBold>
              {t("login.title")}
            </Typography>
            <Typography textNeutral100>
              {t("login.subtitle")}
            </Typography>
          </Box>
          <LoginFormComponent
            form={form}
            onSubmitForm={onSubmitForm}
            goToResetPassword={goToResetPassword}
          />
        </>
      )}
    </Box>
  );
};

export default LoginForm;
